import { useEffect, useState } from 'react';
import { Alert, Button, Segmented, Typography } from 'antd';
import { CloseCircleFilled, WarningFilled } from '@ant-design/icons';
import { StatusPill, type PillTone } from '@/components/StatusPill';
import { colors } from '@/theme/tokens';
import type { ToolCallEvidence, TurnEvidence, ValidationResultEvidence } from '@/api/validationRuns';
import { doubledCallWords } from '@/features/scenario-intelligence/scenarioWords';
import {
  formatMs,
  formatUsd,
  recordedUsage,
  shortHash,
  type AvailableBefore,
  type BriefElementEvidence,
  type ConsultationEvidence,
  type KnowledgeSearchEvidence,
  type ModelCallEvidence,
  type ModelRoundEvidence,
  type TimelineStep,
  type TurnRuntimeEvidence,
} from '@/api/runEvidence';

const { Text } = Typography;
const MONO = "'JetBrains Mono', monospace";

// ─────────────────────────────────────────────────────────────────────────────
// The Debugger (SI-03b, screen 08): one turn read in the order it happened. It opens on the turn the finding points
// at, never on a turn that looks better; later turns are links. Chronological is the default — what the agent's model
// had BEFORE each response (brief, skills, tool results, knowledge, the round's own measures), what that response
// asked for, what came back, the peer's own work nested under the consultation that made it — and "Group by evidence
// type" reads one collector across the turn. Every row is the server's (TurnTimeline, TurnRuntimeEvidence); a gap
// says it is a gap: a turn recorded before the collectors reads "Not recorded", never "none".
// ─────────────────────────────────────────────────────────────────────────────

type View = 'Chronological' | 'Grouped';

const ROLE_LABEL: Record<string, string> = { Agent: 'Agent', Peer: 'Peer', Router: 'Skill router', Other: 'Other' };

/** The turn a finding points at: its first failed check, else a turn that did not complete, else the first turn. */
export function findingTurnOf(evidence: ValidationResultEvidence): number {
  const failedObligation = evidence.obligations?.obligations.find((o) => o.state === 'Fail' && o.turnIndex != null);
  if (failedObligation?.turnIndex != null) return failedObligation.turnIndex;
  const failedTurn = evidence.turns.find((t) => t.assertionsPassed === false || !!t.error);
  return failedTurn?.turnIndex ?? evidence.turns[0]?.turnIndex ?? 0;
}

export function DebuggerPanel({
  evidence,
  turnIndex,
  onTurnChange,
  isDark,
}: {
  evidence: ValidationResultEvidence;
  turnIndex: number;
  onTurnChange: (turnIndex: number) => void;
  isDark: boolean;
}) {
  const [view, setView] = useState<View>('Chronological');
  const turns = evidence.turns;
  const turn = turns.find((t) => t.turnIndex === turnIndex) ?? turns[0];
  const findingTurn = findingTurnOf(evidence);
  const failed = !evidence.passed;

  // [ and ] move between turns, as the mockup's shortcut says.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      const at = turns.findIndex((t) => t.turnIndex === turn?.turnIndex);
      if (e.key === ']' && at >= 0 && at < turns.length - 1) onTurnChange(turns[at + 1].turnIndex);
      if (e.key === '[' && at > 0) onTurnChange(turns[at - 1].turnIndex);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [turns, turn, onTurnChange]);

  if (!turn) return <Text type="secondary">No turn was recorded for this result, so there is nothing to step through.</Text>;

  const firstFail = evidence.obligations?.obligations.find((o) => o.state === 'Fail' && o.turnIndex === findingTurn);
  // A run that stopped is not a failed check: the strip says where it stopped, in grey, never "Fail".
  const stoppedOnly = !firstFail && !evidence.turns.some((t) => t.failures.length > 0) && (!!evidence.traceError || turns.some((t) => !!t.error));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="debugger">
      {failed && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            padding: '8px 12px',
            borderRadius: 10,
            background: turn.turnIndex === findingTurn && !stoppedOnly ? (isDark ? `${colors.red[700]}33` : colors.red[100]) : isDark ? colors.slate[800] : colors.slate[100],
            border: `1px solid ${turn.turnIndex === findingTurn && !stoppedOnly ? `${colors.red[600]}66` : isDark ? colors.slate[700] : colors.slate[200]}`,
          }}
          data-testid="debugger-finding-strip"
        >
          {turn.turnIndex === findingTurn ? (
            <>
              <StatusPill tone={stoppedOnly ? 'slate' : 'red'}>{stoppedOnly ? 'Not assessed' : 'Fail'}</StatusPill>
              <Text strong style={{ fontSize: 13 }}>
                {stoppedOnly ? 'The run stopped on turn ' : 'The finding is on turn '}
                {findingTurn + 1}
                {firstFail ? ` · expected ${firstFail.expected.replace(/^Turn \d+: /, '')}` : ''}
              </Text>
            </>
          ) : (
            <>
              <Text style={{ fontSize: 13 }}>
                Turn {turn.turnIndex + 1} of the scenario. <strong>The finding is on turn {findingTurn + 1}</strong>; nothing on this
                turn changes that.
              </Text>
              <Button type="link" size="small" onClick={() => onTurnChange(findingTurn)}>
                ← Back to turn {findingTurn + 1}
              </Button>
            </>
          )}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Text type="secondary" style={{ fontSize: 12 }}>View</Text>
        <Segmented<View>
          size="small"
          value={view}
          onChange={(v) => setView(v)}
          options={[
            { label: 'Chronological', value: 'Chronological' },
            { label: 'Group by evidence type', value: 'Grouped' },
          ]}
        />
      </div>

      <div className="si-dbg-layout" style={{ display: 'grid', gridTemplateColumns: '170px minmax(0, 1fr)', gap: 12 }}>
        <TurnRail
          turns={turns}
          current={turn.turnIndex}
          findingTurn={failed ? findingTurn : null}
          findingLabel={stoppedOnly ? 'The run stopped here' : 'The finding is here'}
          onPick={onTurnChange}
          isDark={isDark}
        />
        <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <TurnHeader turn={turn} isDark={isDark} />
          {turn.runtime?.recorded === false || !turn.runtime ? (
            <LegacyTurn turn={turn} isDark={isDark} />
          ) : view === 'Chronological' ? (
            <Chronological turn={turn} runtime={turn.runtime} isDark={isDark} />
          ) : (
            <Grouped turn={turn} runtime={turn.runtime} isDark={isDark} />
          )}
          <Reply turn={turn} isDark={isDark} />
          {turn.runtime?.recorded !== false && turn.runtime && <TurnTotals runtime={turn.runtime} isDark={isDark} />}
          <CannotShow turn={turn} />
        </div>
      </div>
      <style>{`@media (max-width: 640px) { .si-dbg-layout { grid-template-columns: minmax(0,1fr) !important; } .si-dbg-rail { flex-direction: row !important; overflow-x: auto; } .si-dbg-rail > button { min-width: 150px; } .si-dbg-shortcut { display: none; } }`}</style>
    </div>
  );
}

// ── The rail ────────────────────────────────────────────────────────────────

function TurnRail({
  turns,
  current,
  findingTurn,
  findingLabel,
  onPick,
  isDark,
}: {
  turns: TurnEvidence[];
  current: number;
  findingTurn: number | null;
  findingLabel: string;
  onPick: (turnIndex: number) => void;
  isDark: boolean;
}) {
  return (
    <div className="si-dbg-rail" style={{ display: 'flex', flexDirection: 'column', gap: 8 }} role="tablist" aria-label="Turns">
      {turns.map((t) => {
        const recorded = !!t.runtime && t.runtime.recorded !== false;
        const rounds = recorded ? t.runtime!.modelCalls.filter((c) => c.role === 'Agent').reduce((n, c) => n + Math.max(1, c.rounds?.length ?? 0), 0) : null;
        const consults = t.runtime?.consultations.length ?? 0;
        const notDone = t.toolCalls.filter((c) => c.simulated).length;
        const selected = t.turnIndex === current;
        return (
          <button
            key={t.turnIndex}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onPick(t.turnIndex)}
            style={{
              textAlign: 'left',
              padding: '8px 10px',
              borderRadius: 10,
              cursor: 'pointer',
              border: `1.5px solid ${selected ? colors.ink[600] : isDark ? colors.slate[700] : colors.slate[200]}`,
              background: isDark ? colors.slate[800] : '#FFFFFF',
              color: 'inherit',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <strong style={{ fontSize: 13 }}>Turn {t.turnIndex + 1}</strong>
              <TurnPill turn={t} />
            </div>
            <div style={{ fontSize: 11.5, color: colors.slate[500], marginTop: 3, lineHeight: 1.4 }}>
              {formatMs(t.elapsedMs)}
              {rounds != null ? ` · ${rounds} round${rounds === 1 ? '' : 's'}` : ''} · {t.toolCalls.length} tool{t.toolCalls.length === 1 ? '' : 's'}
              <br />
              {recorded ? (consults ? `${consults} consult${consults === 1 ? '' : 's'}` : 'No peer consult') : 'Not recorded'}
              {notDone ? ` · ${notDone} not carried out` : ''}
            </div>
            {findingTurn === t.turnIndex && <div style={{ fontSize: 11.5, color: findingLabel === 'The finding is here' ? colors.red[600] : colors.slate[500], marginTop: 2 }}>{findingLabel}</div>}
          </button>
        );
      })}
      <Text className="si-dbg-shortcut" type="secondary" style={{ fontSize: 11 }}>
        Shortcut: [ ] between turns
      </Text>
    </div>
  );
}

function TurnPill({ turn }: { turn: TurnEvidence }) {
  if (turn.error) return <StatusPill tone="slate">Did not complete</StatusPill>;
  if (turn.assertionsPassed === true) return <StatusPill tone="green">Checks passed</StatusPill>;
  if (turn.assertionsPassed === false) return <StatusPill tone="red">Checks failed</StatusPill>;
  return <StatusPill tone="slate">Not checked</StatusPill>;
}

function TurnHeader({ turn, isDark }: { turn: TurnEvidence; isDark: boolean }) {
  const rt = turn.runtime;
  const recorded = rt && rt.recorded !== false;
  const agentRounds = rt?.modelCalls.filter((c) => c.role === 'Agent').reduce((n, c) => n + Math.max(1, c.rounds?.length ?? 0), 0) ?? 0;
  return (
    <div style={panel(isDark)}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <strong>Turn {turn.turnIndex + 1}</strong>
        <Text type="secondary" style={{ fontSize: 12 }}>Patient</Text>
        <Text type="secondary" style={{ marginLeft: 'auto', fontSize: 12 }}>{formatMs(turn.elapsedMs)} end to end</Text>
      </div>
      <div style={{ fontSize: 14, margin: '4px 0 8px', lineHeight: 1.5 }}>“{turn.userMessage ?? '(nothing recorded)'}”</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {turn.correlationId ? (
          <span style={chip(isDark)} title={turn.correlationId}>
            correlation <span style={{ fontFamily: MONO }}>{shortHash(turn.correlationId.replace(/-/g, ''))}</span>
          </span>
        ) : (
          <span style={chip(isDark)}>correlation not recorded</span>
        )}
        {recorded && <span style={chip(isDark)}>{agentRounds} agent model round{agentRounds === 1 ? '' : 's'}</span>}
        {recorded && <span style={chip(isDark)}>{recordedUsage(rt!.totals.costUsd ?? pricedSum(rt!.modelCalls), rt!.totals.unpricedCalls)}</span>}
      </div>
    </div>
  );
}

// ── Chronological ──────────────────────────────────────────────────────────

function Chronological({ turn, runtime, isDark }: { turn: TurnEvidence; runtime: TurnRuntimeEvidence; isDark: boolean }) {
  const steps = turn.timeline?.steps ?? [];
  const top = steps.filter((s) => s.withinToolOrder == null);
  if (steps.length === 0)
    return <Text type="secondary">Nothing was recorded under this turn's trace: no model call, tool, search or consultation.</Text>;
  return (
    <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }} data-testid="timeline">
      {top.map((step, i) => (
        <li key={i}>
          <StepView step={step} steps={steps} turn={turn} runtime={runtime} isDark={isDark} />
        </li>
      ))}
    </ol>
  );
}

function StepView({
  step,
  steps,
  turn,
  runtime,
  isDark,
  nested,
}: {
  step: TimelineStep;
  steps: TimelineStep[];
  turn: TurnEvidence;
  runtime: TurnRuntimeEvidence;
  isDark: boolean;
  nested?: boolean;
}) {
  const call = step.callIndex != null ? runtime.modelCalls[step.callIndex] : undefined;
  const at = <AtLabel step={step} />;
  switch (step.kind) {
    case 'Router':
      return (
        <StepCard isDark={isDark} at={at} title={`Skill router${call?.model ? ` · ${call.model}` : turn.router?.model ? ` · ${turn.router.model}` : ''}`} duration={step.durationMs} concurrent={step.concurrent}>
          {turn.router ? (
            turn.router.skills.length === 0 ? (
              <Text type="secondary" style={{ fontSize: 12.5 }}>Supplied no skill on this turn{turn.router.failureReason ? ` (${turn.router.failureReason})` : ''}.</Text>
            ) : (
              <ul style={listStyle}>
                {turn.router.skills.map((s) => (
                  <li key={s.name}>
                    <span style={{ fontFamily: MONO }}>{s.name}</span>
                    {s.alwaysLoad ? <Text type="secondary"> · always loaded</Text> : s.reason ? <Text type="secondary"> · “{s.reason}”</Text> : null}
                  </li>
                ))}
              </ul>
            )
          ) : (
            <Text type="secondary" style={{ fontSize: 12.5 }}>The router's decision was not recorded.</Text>
          )}
          {call && <CallFoot call={call} />}
        </StepCard>
      );
    case 'ModelRound': {
      if (!call) return null;
      const round = step.roundIndex != null ? call.rounds?.find((r) => r.roundIndex === step.roundIndex) : undefined;
      const multi = (call.rounds?.length ?? 0) > 1;
      const isAgent = call.role === 'Agent';
      return (
        <StepCard
          isDark={isDark}
          at={at}
          nested={nested}
          title={`${isAgent ? '' : `${ROLE_LABEL[call.role] ?? call.role} · `}Model round${step.roundIndex != null ? ` ${step.roundIndex + 1}` : ''} · ${round?.model ?? call.model ?? 'deployment not recorded'}`}
          duration={step.durationMs}
          concurrent={step.concurrent}
          approximate={step.startApproximate}
        >
          {step.available && <AvailableBox a={step.available} first={(step.roundIndex ?? 0) === 0} call={call} turn={turn} runtime={runtime} isDark={isDark} />}
          <RoundMetrics call={call} round={round} multi={multi} isDark={isDark} />
          {round && (
            <Text style={{ display: 'block', fontSize: 12.5, marginTop: 6 }}>
              {round.toolsInvoked > 0
                ? `This response asked for ${round.toolsInvoked} tool${round.toolsInvoked === 1 ? '' : 's'}.`
                : round.finishReason
                  ? `This response asked for no tool (finish: ${round.finishReason}).`
                  : 'This response asked for no tool.'}
            </Text>
          )}
        </StepCard>
      );
    }
    case 'Tool': {
      const tool = turn.toolCalls.find((t) => t.order === step.toolOrder);
      if (!tool) return null;
      const children = steps.filter((s) => s.withinToolOrder === tool.order);
      return (
        <StepCard isDark={isDark} at={at} title={tool.toolName} duration={step.durationMs ?? tool.durationMs} concurrent={step.concurrent} pill={<ToolOutcome tool={tool} />}>
          <ToolPayloads tool={tool} isDark={isDark} />
          {children.length > 0 && (
            <div style={{ marginTop: 8, paddingLeft: 12, borderLeft: `2px solid ${colors.violet[500]}55`, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <Text type="secondary" style={{ fontSize: 11.5 }}>The peer's own work · same correlation id</Text>
              {children.map((c, i) => (
                <StepView key={i} step={c} steps={steps} turn={turn} runtime={runtime} isDark={isDark} nested />
              ))}
            </div>
          )}
        </StepCard>
      );
    }
    case 'KnowledgeSearch': {
      const search = step.searchIndex != null ? runtime.knowledgeSearches[step.searchIndex] : undefined;
      if (!search) return null;
      return (
        <StepCard isDark={isDark} at={at} nested={nested} title={`Knowledge search${step.actor === 'Peer' ? ' · by the peer' : ''}`} duration={step.durationMs} concurrent={step.concurrent}>
          <SearchView search={search} isDark={isDark} />
        </StepCard>
      );
    }
    case 'Consultation': {
      const consult = step.consultationIndex != null ? runtime.consultations[step.consultationIndex] : undefined;
      if (!consult) return null;
      return (
        <StepCard isDark={isDark} at={at} nested={nested} title={`Peer consult · ${consult.peer ?? 'peer not recorded'}`} duration={step.durationMs} concurrent={step.concurrent} pill={<ConsultOutcome c={consult} />}>
          <ConsultView c={consult} isDark={isDark} />
        </StepCard>
      );
    }
    default:
      return null;
  }
}

function AtLabel({ step }: { step: TimelineStep }) {
  if (step.atMs == null) return <span>time not recorded</span>;
  return (
    <span>
      {step.startApproximate ? '≈' : '+'}
      {(step.atMs / 1000).toFixed(1)} s
    </span>
  );
}

function AvailableBox({
  a,
  first,
  call,
  turn,
  runtime,
  isDark,
}: {
  a: AvailableBefore;
  first: boolean;
  call: ModelCallEvidence;
  turn: TurnEvidence;
  runtime: TurnRuntimeEvidence;
  isDark: boolean;
}) {
  const toolName = (order: number) => turn.toolCalls.find((t) => t.order === order)?.toolName ?? `tool ${order + 1}`;
  const row = (label: string, value: React.ReactNode) => (
    <div style={{ display: 'grid', gridTemplateColumns: '110px minmax(0,1fr)', gap: 8, fontSize: 12.5, padding: '3px 0' }}>
      <Text type="secondary" style={{ fontSize: 12 }}>{label}</Text>
      <div style={{ minWidth: 0, wordBreak: 'break-word' }}>{value}</div>
    </div>
  );
  const skillsText = (list: AvailableBefore['skills']) =>
    list.length === 0 ? 'none' : list.map((s) => `${s.name} (${s.source === 'LoadSkill' ? 'loaded by the model' : s.source})`).join(', ');
  const faults = call.brief.filter((b) => !['Rendered', 'Empty', 'Deduped'].includes(b.state));
  return (
    <div
      style={{ border: `1px dashed ${isDark ? colors.slate[600] : colors.slate[300]}`, borderRadius: 8, padding: '6px 10px', marginBottom: 8 }}
      data-testid="available-before"
    >
      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: colors.slate[500], marginBottom: 2 }}>
        {first ? 'Available before this response' : 'Available before this response: what is new since the last round'}
      </div>
      {first ? (
        <>
          {row(
            'Patient brief',
            a.briefElements === 0 ? (
              <span>No element recorded on this call: either no brief is linked or its rows were not written.</span>
            ) : (
              <span>
                {a.briefElements} elements: {a.briefRendered} rendered, {a.briefEmpty} empty, {a.briefDeduped} deduped, {a.briefFaults} fault
                {a.briefFaults === 1 ? '' : 's'}
                {faults.length > 0 && (
                  <span style={{ color: colors.red[600] }}> · {faults.map((f) => `${f.elementCode} (${f.state})`).join(', ')}</span>
                )}
                <BriefTable elements={call.brief} isDark={isDark} />
              </span>
            ),
          )}
          {row('Skills in prompt', skillsText(a.skills))}
          {row('Tools offered', a.toolsOffered != null ? `${a.toolsOffered} (names are not recorded per round)` : 'Not recorded')}
          {row('Knowledge', a.knowledgeSearches.length === 0 ? 'Not searched before this response' : `${a.knowledgeSearches.length} search(es) had returned`)}
          {row('Tool results', a.toolResults.length === 0 ? 'none yet' : a.toolResults.map(toolName).join(', '))}
        </>
      ) : (
        <>
          {row('New skills', a.newSkills.length === 0 ? 'none' : skillsText(a.newSkills))}
          {row('New tool results', a.newToolResults.length === 0 ? 'none' : a.newToolResults.map(toolName).join(', '))}
          {row('New knowledge', a.newKnowledgeSearches.length === 0 ? 'none' : `${a.newKnowledgeSearches.length} search(es)`)}
          {a.newConsultations.length > 0 && row('New consults', a.newConsultations.map((i) => runtime.consultations[i]?.peer ?? 'peer').join(', '))}
          {row('Unchanged', `The same brief, ${a.skills.length - a.newSkills.length} earlier skill${a.skills.length - a.newSkills.length === 1 ? '' : 's'}, ${a.toolsOffered ?? 'an unrecorded number of'} tools offered.`)}
        </>
      )}
      {row(
        'Prompt',
        <span>
          {a.promptHash ? <span style={{ fontFamily: MONO }}>sha256 {shortHash(a.promptHash)}</span> : 'hash not recorded'}
          {a.estimatedInputTokens != null ? ` · ≈${a.estimatedInputTokens.toLocaleString()} tokens (estimate)` : ''}
          {a.samePromptPrefix === true ? ' · same system prefix as the last round' : a.samePromptPrefix === false ? ' · system prefix changed' : ''}
        </span>,
      )}
    </div>
  );
}

function BriefTable({ elements, isDark }: { elements: BriefElementEvidence[]; isDark: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      {' '}
      <Button type="link" size="small" style={{ padding: 0, height: 'auto', fontSize: 12 }} onClick={() => setOpen((v) => !v)}>
        {open ? 'Hide elements' : `Show all ${elements.length}`}
      </Button>
      {open && (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 4, fontSize: 12 }}>
          <tbody>
            {elements.map((b, i) => (
              <tr key={i} style={{ borderTop: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}` }}>
                <td style={{ padding: '3px 4px', fontFamily: MONO, wordBreak: 'break-all' }}>{b.briefingCode}</td>
                <td style={{ padding: '3px 4px', fontFamily: MONO, wordBreak: 'break-all' }}>{b.elementCode}</td>
                <td style={{ padding: '3px 4px' }}>
                  <StatusPill tone={briefTone(b.state)}>{b.state}</StatusPill>
                </td>
                <td style={{ padding: '3px 4px', color: colors.slate[500] }}>
                  {b.state === 'Deduped'
                    ? `won by ${b.dedupedFrom}`
                    : b.state === 'Rendered'
                      ? b.tokenEstimate > 0
                        ? `${b.tokenEstimate} tokens`
                        : 'tokens not recorded'
                      : b.state === 'Empty'
                        ? 'nothing on file'
                        : 'renderer failed'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function RoundMetrics({ call, round, multi, isDark }: { call: ModelCallEvidence; round?: ModelRoundEvidence; multi: boolean; isDark: boolean }) {
  const cell = (label: string, value: React.ReactNode) => (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 10.5, color: colors.slate[500], textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: 12.5, fontWeight: 600, wordBreak: 'break-word' }}>{value}</div>
    </div>
  );
  const input = round ? round.inputTokens : call.inputTokens;
  const cached = round ? round.cachedInputTokens : call.cachedTokens;
  const output = round ? round.outputTokens : call.outputTokens;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))',
        gap: 8,
        padding: '6px 0 0',
        borderTop: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
      }}
    >
      {cell('Deployment', round?.model ?? call.model ?? 'Not recorded')}
      {cell('Route', call.route ?? '—')}
      {cell('Input (cached)', input == null ? 'not reported' : `${input.toLocaleString()}${cached != null ? ` (${cached.toLocaleString()})` : ''}`)}
      {cell('Output', output == null ? 'not reported' : output.toLocaleString())}
      {cell(
        'Cost',
        multi ? (
          <span style={{ fontWeight: 400 }}>recorded per call: {call.costUsd != null ? formatUsd(call.costUsd) : 'Price unavailable'} for {call.rounds!.length} rounds</span>
        ) : call.costUsd != null ? (
          formatUsd(call.costUsd)
        ) : (
          <StatusPill tone="slate">Price unavailable</StatusPill>
        ),
      )}
    </div>
  );
}

function CallFoot({ call }: { call: ModelCallEvidence }) {
  return (
    <Text type="secondary" style={{ display: 'block', fontSize: 11.5, marginTop: 6 }}>
      {call.inputTokens.toLocaleString()} in / {call.outputTokens.toLocaleString()} out · {call.costUsd != null ? formatUsd(call.costUsd) : 'Price unavailable, counted not priced'}
    </Text>
  );
}

function ToolOutcome({ tool }: { tool: ToolCallEvidence }) {
  if (tool.simulated) return <StatusPill tone="amber">Not carried out</StatusPill>;
  if (tool.outcome === 'Doubled') return <StatusPill tone="ink">Scenario’s double</StatusPill>;
  if (tool.outcome === 'NoResult') return <StatusPill tone="red">No result</StatusPill>;
  return null;
}

function ToolPayloads({ tool, isDark }: { tool: ToolCallEvidence; isDark: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      {tool.simulated && (
        <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
          A test run does not carry out the agent's own tools that change a record or send something: the run answered for
          the tool and nothing was sent.
        </Text>
      )}
      {tool.outcome === 'NoResult' && (
        <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>It threw or came back empty; which one is not recorded.</Text>
      )}
      {tool.outcome === 'Doubled' && (
        <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
          {doubledCallWords(tool.label)}. The scenario says what this tool answers, so the tool did not run.
        </Text>
      )}
      <Button type="link" size="small" style={{ paddingLeft: 0 }} onClick={() => setOpen((v) => !v)}>
        {open ? 'Hide what was sent and returned' : 'Show what was sent and returned'}
      </Button>
      {open && (
        <>
          <Payload title="Sent" body={tool.request} cut={tool.requestTruncated} isDark={isDark} />
          <Payload title={tool.simulated ? "Returned (by the run's double)" : tool.outcome === 'Doubled' ? "Returned (by the scenario's double)" : 'Returned'}
            body={tool.response} cut={tool.responseTruncated} isDark={isDark} />
        </>
      )}
    </div>
  );
}

function SearchView({ search, isDark }: { search: KnowledgeSearchEvidence; isDark: boolean }) {
  return (
    <div>
      <Text style={{ fontSize: 12.5 }}>
        {search.query ? <>query “{search.query}” · </> : null}
        {search.outcome ?? 'outcome not recorded'} · {search.resultCount} passage{search.resultCount === 1 ? '' : 's'}
      </Text>
      {search.passages.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6, fontSize: 12 }}>
            <thead>
              <tr style={{ color: colors.slate[500], textAlign: 'left' }}>
                <th style={th}>Document</th>
                <th style={th}>Chunk</th>
                <th style={th}>Score</th>
                <th style={th}>Rerank</th>
              </tr>
            </thead>
            <tbody>
              {search.passages.map((p, i) => (
                <tr key={i} style={{ borderTop: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}` }}>
                  <td style={td}>
                    {p.fileName ?? p.documentId ?? '—'}
                    {p.page != null ? ` · p.${p.page}` : ''}
                  </td>
                  <td style={{ ...td, fontFamily: MONO }}>{p.chunkId ?? '—'}</td>
                  <td style={td}>{p.score != null ? p.score.toFixed(3) : '—'}</td>
                  <td style={td}>{p.rerankerScore != null ? p.rerankerScore.toFixed(2) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ConsultOutcome({ c }: { c: ConsultationEvidence }) {
  if (c.timedOut) return <StatusPill tone="slate">Timed out</StatusPill>;
  if (!c.succeeded) return <StatusPill tone="red">Failed</StatusPill>;
  return <StatusPill tone="green">Answered</StatusPill>;
}

function ConsultView({ c, isDark }: { c: ConsultationEvidence; isDark: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        depth {c.depth} · {formatMs(c.elapsedMs)}
        {c.timedOut ? ' · its configured timeout was reached: an integration failure, not the agent’s' : ' · timeout not reached'}
        {c.error ? ` · ${c.error}` : ''}
      </Text>
      {c.question && <Payload title="Question sent" body={c.question} cut={false} isDark={isDark} />}
      {c.reply && <Payload title="Reply · not reviewed clinical content" body={c.reply} cut={!!c.replyCut} isDark={isDark} />}
    </div>
  );
}

// ── Grouped ────────────────────────────────────────────────────────────────

function Grouped({ turn, runtime, isDark }: { turn: TurnEvidence; runtime: TurnRuntimeEvidence; isDark: boolean }) {
  const agentCalls = runtime.modelCalls.filter((c) => c.role === 'Agent');
  const brief = agentCalls.find((c) => c.brief.length > 0)?.brief ?? [];
  const steps = turn.timeline?.steps ?? [];
  const at = (pred: (s: TimelineStep) => boolean) => {
    const s = steps.find(pred);
    return s?.atMs != null ? `+${(s.atMs / 1000).toFixed(1)} s` : '—';
  };
  const skills = steps.find((s) => s.available)?.available;
  const lastAvailable = [...steps].reverse().find((s) => s.available)?.available;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} data-testid="grouped">
      <Block title={`Model rounds · ${runtime.modelCalls.reduce((n, c) => n + Math.max(1, c.rounds?.length ?? 0), 0)}`} isDark={isDark}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ color: colors.slate[500], textAlign: 'left' }}>
              <th style={th}>Who</th>
              <th style={th}>Deployment</th>
              <th style={th}>Started</th>
              <th style={th}>In (cached) / out</th>
              <th style={th}>Cost</th>
            </tr>
          </thead>
          <tbody>
            {runtime.modelCalls.flatMap((c, ci) =>
              (c.rounds && c.rounds.length > 0 ? c.rounds : [null]).map((r, ri) => (
                <tr key={`${ci}-${ri}`} style={{ borderTop: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}` }}>
                  <td style={td}>{ROLE_LABEL[c.role] ?? c.role}{r ? ` · round ${r.roundIndex + 1}` : ''}</td>
                  <td style={{ ...td, fontFamily: MONO }}>{r?.model ?? c.model ?? '—'}</td>
                  <td style={td}>{at((s) => s.callIndex === ci && (r == null || s.roundIndex === r.roundIndex))}</td>
                  <td style={td}>
                    {(r?.inputTokens ?? c.inputTokens)?.toLocaleString() ?? '—'}
                    {(r?.cachedInputTokens ?? c.cachedTokens) ? ` (${(r?.cachedInputTokens ?? c.cachedTokens)!.toLocaleString()})` : ''} /{' '}
                    {(r?.outputTokens ?? c.outputTokens)?.toLocaleString() ?? '—'}
                  </td>
                  <td style={td}>{ri > 0 ? '' : c.costUsd != null ? formatUsd(c.costUsd) : 'Price unavailable'}</td>
                </tr>
              )),
            )}
          </tbody>
        </table>
      </Block>
      <Block title={`Tool calls · ${turn.toolCalls.length}${turn.toolCalls.some((t) => t.simulated) ? ` · ${turn.toolCalls.filter((t) => t.simulated).length} not carried out` : ''}`} isDark={isDark}>
        {turn.toolCalls.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 12.5 }}>No tool call was recorded on this turn.</Text>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <tbody>
              {turn.toolCalls.map((t) => (
                <tr key={t.order} style={{ borderTop: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}` }}>
                  <td style={td}>{t.order + 1}</td>
                  <td style={{ ...td, fontFamily: MONO, wordBreak: 'break-all' }}>{t.toolName}</td>
                  <td style={td}>{at((s) => s.toolOrder === t.order && s.kind === 'Tool')}</td>
                  <td style={td}>{t.durationMs != null ? formatMs(t.durationMs) : '—'}</td>
                  <td style={td}>{t.simulated || t.outcome === 'Doubled' ? <ToolOutcome tool={t} /> : t.outcome ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Block>
      <Block title={`Skills · ${lastAvailable?.skills.length ?? 0} in the prompt by the last round`} isDark={isDark}>
        {!lastAvailable || lastAvailable.skills.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 12.5 }}>{skills ? 'No skill was in the prompt.' : 'Not recorded for this turn.'}</Text>
        ) : (
          <ul style={listStyle}>
            {lastAvailable.skills.map((s) => (
              <li key={s.name}>
                <span style={{ fontFamily: MONO }}>{s.name}</span>
                <Text type="secondary"> · {s.source === 'LoadSkill' ? `loaded by the model (tool ${(s.toolOrder ?? 0) + 1})` : s.source}</Text>
              </li>
            ))}
          </ul>
        )}
      </Block>
      <Block title={`Knowledge · ${runtime.knowledgeSearches.length} search${runtime.knowledgeSearches.length === 1 ? '' : 'es'}`} isDark={isDark}>
        {runtime.knowledgeSearches.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 12.5 }}>Not searched on this turn.</Text>
        ) : (
          runtime.knowledgeSearches.map((s, i) => <SearchView key={i} search={s} isDark={isDark} />)
        )}
      </Block>
      <Block title={`Patient brief · ${brief.length} element${brief.length === 1 ? '' : 's'}`} isDark={isDark}>
        {brief.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 12.5 }}>No element was recorded on the agent's calls.</Text>
        ) : (
          <span style={{ fontSize: 12.5 }}>
            {brief.filter((b) => b.state === 'Rendered').length} rendered, {brief.filter((b) => b.state === 'Empty').length} empty,{' '}
            {brief.filter((b) => b.state === 'Deduped').length} deduped, {brief.filter((b) => !['Rendered', 'Empty', 'Deduped'].includes(b.state)).length} fault
            <BriefTable elements={brief} isDark={isDark} />
          </span>
        )}
      </Block>
      <Block title={`Peer consult · ${runtime.consultations.length}`} isDark={isDark}>
        {runtime.consultations.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 12.5 }}>No peer consult on this turn.</Text>
        ) : (
          runtime.consultations.map((c, i) => (
            <div key={i} style={{ marginBottom: 6 }}>
              <Text strong style={{ fontSize: 12.5 }}>{c.peer}</Text> <ConsultOutcome c={c} />
              <ConsultView c={c} isDark={isDark} />
            </div>
          ))
        )}
      </Block>
    </div>
  );
}

// ── Reply, totals, legacy, notes ───────────────────────────────────────────

function Reply({ turn, isDark }: { turn: TurnEvidence; isDark: boolean }) {
  return (
    <div style={panel(isDark)}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
        <strong style={{ fontSize: 13 }}>Reply sent</strong>
        <StatusPill tone="slate">illustrative · not reviewed clinical content</StatusPill>
      </div>
      <div style={{ whiteSpace: 'pre-wrap', fontSize: 13.5, lineHeight: 1.55 }}>{turn.agentReply || <i style={{ opacity: 0.6 }}>(nothing recorded)</i>}</div>
      {turn.error && <Alert type="warning" showIcon style={{ marginTop: 8 }} title="This turn did not complete" description={turn.error} />}
      {turn.failures.map((f, i) => (
        <div
          key={i}
          style={{
            marginTop: 8,
            padding: '6px 10px',
            borderRadius: 8,
            background: isDark ? `${colors.red[700]}33` : colors.red[100],
            border: `1px solid ${colors.red[600]}55`,
            fontSize: 12.5,
          }}
        >
          <CloseCircleFilled style={{ color: colors.red[600], marginRight: 6 }} />
          <strong>Not met here:</strong> <span style={{ fontFamily: MONO }}>{f.check}</span> — {f.detail}
        </div>
      ))}
    </div>
  );
}

function TurnTotals({ runtime, isDark }: { runtime: TurnRuntimeEvidence; isDark: boolean }) {
  const roles = ['Agent', 'Peer', 'Router', 'Other'] as const;
  const rows = roles
    .map((role) => {
      const calls = runtime.modelCalls.filter((c) => c.role === role);
      if (calls.length === 0) return null;
      const priced = calls.filter((c) => c.costUsd != null);
      return {
        role,
        model: [...new Set(calls.map((c) => c.model ?? 'unrecorded'))].join(', '),
        rounds: calls.reduce((n, c) => n + Math.max(1, c.rounds?.length ?? 0), 0),
        input: calls.reduce((n, c) => n + c.inputTokens, 0),
        cached: calls.reduce((n, c) => n + c.cachedTokens, 0),
        output: calls.reduce((n, c) => n + c.outputTokens, 0),
        ms: calls.reduce((n, c) => n + c.durationMs, 0),
        cost: priced.length ? priced.reduce((n, c) => n + (c.costUsd ?? 0), 0) : null,
        unpriced: calls.length - priced.length,
      };
    })
    .filter(Boolean) as Array<{ role: string; model: string; rounds: number; input: number; cached: number; output: number; ms: number; cost: number | null; unpriced: number }>;
  const unpriced = rows.reduce((n, r) => n + r.unpriced, 0);
  return (
    <div style={panel(isDark)} data-testid="turn-totals">
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline', marginBottom: 6 }}>
        <strong style={{ fontSize: 13 }}>Turn totals</strong>
        <Text style={{ fontSize: 12.5 }}>{recordedUsage(rows.some((r) => r.cost != null) ? rows.reduce((n, r) => n + (r.cost ?? 0), 0) : null, unpriced)}</Text>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ color: colors.slate[500], textAlign: 'left' }}>
              <th style={th}>Who</th>
              <th style={th}>Rounds</th>
              <th style={th}>Input (cached) / output</th>
              <th style={th}>Model time</th>
              <th style={th}>Cost</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.role} style={{ borderTop: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}` }}>
                <td style={td}>
                  {ROLE_LABEL[r.role] ?? r.role} · <span style={{ fontFamily: MONO }}>{r.model}</span>
                </td>
                <td style={td}>{r.rounds}</td>
                <td style={td}>
                  {r.input.toLocaleString()} ({r.cached.toLocaleString()}) / {r.output.toLocaleString()}
                </td>
                <td style={td}>{formatMs(r.ms)}</td>
                <td style={td}>
                  {r.cost != null ? formatUsd(r.cost) : <StatusPill tone="slate">Price unavailable</StatusPill>}
                  {r.cost != null && r.unpriced > 0 ? ` + ${r.unpriced} unpriced` : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Text type="secondary" style={{ display: 'block', fontSize: 11.5, marginTop: 4 }}>
        Model time is the calls' own duration; it is not the turn's elapsed time. No evaluator call is made: every check
        here is deterministic. An unpriced call is counted, never added as $0.
      </Text>
    </div>
  );
}

function LegacyTurn({ turn, isDark }: { turn: TurnEvidence; isDark: boolean }) {
  const row = (label: string, value: string) => (
    <div style={{ display: 'flex', gap: 8, fontSize: 12.5, padding: '3px 0' }}>
      <span style={{ minWidth: 120, color: colors.slate[500] }}>{label}</span>
      <span style={{ minWidth: 0, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{value}</span>
    </div>
  );
  return (
    <div style={panel(isDark)} data-testid="debugger-legacy">
      <Alert
        type="warning"
        showIcon
        icon={<WarningFilled />}
        title="This turn predates the Debugger's collectors"
        description="Rows below say Not recorded. Not recorded is not “did not happen”: the agent may have used the brief, searched knowledge or consulted a peer without this run keeping it."
        style={{ marginBottom: 8 }}
      />
      {row('Model rounds', 'Not recorded · which deployment answered was not kept')}
      {row('Tools', turn.toolCalls.length ? `${turn.toolCalls.length} · ${turn.toolCalls.map((t) => t.toolName).join(', ')} · no duration, no start` : 'none recorded')}
      {row('Skills', turn.router ? 'router decision only · no body hash, no load_skill' : 'Not recorded')}
      {row('Knowledge', 'Not recorded')}
      {row('Patient brief', 'Not recorded')}
      {row('Peer consult', 'Not recorded')}
      {row('Cost', 'Price unavailable, not $0')}
    </div>
  );
}

function CannotShow({ turn }: { turn: TurnEvidence }) {
  const notes = [...(turn.runtime?.notes ?? []), ...(turn.timeline?.notes ?? [])];
  const always = 'The order in which text and cards streamed to the patient.';
  return (
    <Alert
      type="info"
      showIcon
      title="What this record cannot show"
      description={
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {[...new Set([...notes, always])].map((n) => (
            <li key={n}>{n}</li>
          ))}
        </ul>
      }
    />
  );
}

function Payload({ title, body, cut, isDark }: { title: string; body?: string | null; cut: boolean; isDark: boolean }) {
  return (
    <div style={{ marginTop: 4 }}>
      <Text type="secondary" style={{ fontSize: 11.5 }}>
        {title}
        {cut ? ' (cut when stored)' : ''}
      </Text>
      <pre
        style={{
          margin: '2px 0 0',
          padding: 8,
          maxHeight: 180,
          overflow: 'auto',
          fontFamily: MONO,
          fontSize: 11.5,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          borderRadius: 6,
          background: isDark ? colors.slate[900] : colors.slate[100],
        }}
      >
        {body ? body : '(nothing recorded)'}
      </pre>
    </div>
  );
}

function StepCard({
  isDark,
  at,
  title,
  duration,
  concurrent,
  approximate,
  nested,
  pill,
  children,
}: {
  isDark: boolean;
  at: React.ReactNode;
  title: string;
  duration?: number | null;
  concurrent?: boolean;
  approximate?: boolean;
  nested?: boolean;
  pill?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div style={{ ...panel(isDark), padding: nested ? '6px 10px' : '8px 12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: children ? 6 : 0 }}>
        <span style={{ fontFamily: MONO, fontSize: 11.5, color: colors.slate[500], minWidth: 52 }}>{at}</span>
        <strong style={{ fontSize: 13, wordBreak: 'break-word', minWidth: 0 }}>{title}</strong>
        {concurrent && <StatusPill tone="violet">Concurrent</StatusPill>}
        {approximate && <StatusPill tone="slate">Start approximate</StatusPill>}
        {pill}
        {duration != null && <span style={{ marginLeft: 'auto', fontSize: 12, color: colors.slate[500] }}>{formatMs(duration)}</span>}
      </div>
      {children}
    </div>
  );
}

function Block({ title, children, isDark }: { title: string; children: React.ReactNode; isDark: boolean }) {
  return (
    <div style={panel(isDark)}>
      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: colors.slate[500], marginBottom: 6 }}>{title}</div>
      <div style={{ overflowX: 'auto' }}>{children}</div>
    </div>
  );
}

function briefTone(state: string): PillTone {
  if (state === 'Rendered') return 'green';
  if (state === 'Empty') return 'slate';
  if (state === 'Deduped') return 'ink';
  return 'red';
}

function pricedSum(calls: ModelCallEvidence[]): number | null {
  const priced = calls.filter((c) => c.costUsd != null);
  return priced.length ? priced.reduce((n, c) => n + (c.costUsd ?? 0), 0) : null;
}

function panel(isDark: boolean): React.CSSProperties {
  return {
    padding: '10px 12px',
    borderRadius: 10,
    background: isDark ? colors.slate[800] : '#FFFFFF',
    border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
    minWidth: 0,
  };
}

function chip(isDark: boolean): React.CSSProperties {
  return {
    fontSize: 11.5,
    padding: '2px 8px',
    borderRadius: 999,
    background: isDark ? colors.slate[900] : colors.slate[100],
    color: isDark ? colors.slate[300] : colors.slate[600],
  };
}

const listStyle: React.CSSProperties = { margin: 0, paddingLeft: 18, fontSize: 12.5 };
const th: React.CSSProperties = { padding: '4px 6px', fontWeight: 600, fontSize: 11 };
const td: React.CSSProperties = { padding: '4px 6px', verticalAlign: 'top' };

/** For tests: the steps the chronological view draws at the top level (a peer's own work sits inside its consult). */
export function topLevelSteps(steps: TimelineStep[]): TimelineStep[] {
  return steps.filter((s) => s.withinToolOrder == null);
}

